import classNames from 'classnames';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useMobile } from '../../context/mobile/mobile.provider';
import { EditTray } from '../../filters/customFilters/editTray';
import { useCorePageState } from '../../page/provider';
import { DynamicTitle, ListBoxMenuItem, Params, TitleSelector } from '../../types';
import { TrayType } from '../../types/content/custom.types';
import { SelectionMenuComponent } from '../selection.menu/menu';
import { TitleButton } from './title.button';

type Props = {
  title: string | DynamicTitle;
  settings: Params;
  inactive?: boolean;
  onChange: (updates: Params, shouldUpdateDsl?: boolean) => void;
};
function DynamicTitleElement({ title, settings, inactive, onChange }: Readonly<Props>) {
  const entries = useMemo(() => {
    return typeof title === 'string' ? [title] : title;
  }, [title]);

  const { setCustomizationTray, isCollections, isFilterTray, setIsFilterTray } = useCorePageState();

  const entry = entries.find(
    entry => typeof entry === 'object' && entry.menu.customizationTraySettings
  ) as TitleSelector;

  const [groupingLabel, setGroupingLabel] = useState<string>();

  // for setting groupingLabel
  useEffect(() => {
    const groupingMenuEntry = entries.find(
      entry => typeof entry === 'object' && entry.menu && entry.menu.paramName === 'grouping'
    ) as TitleSelector;
    const label = (groupingMenuEntry?.menu?.items?.[0] as ListBoxMenuItem)?.label;
    if (label) {
      setGroupingLabel(label);
    }
  }, [entries]);

  // settings keys to update dsl
  const updateDslKeys = ['scPeriod', 'sgPeriod', 'tsDuration', 'lbPeriod'];

  const handleChange = (updates: Params, key?: string) => {
    if (key === 'grouping') {
      setGroupingLabel(updates.item_label?.[0]);
    } else {
      updates.item_label = [groupingLabel];
    }

    const isChannels = updates.grouping?.[0]?.startsWith('segment_outlet_group_code') ?? false;
    const shouldUpdateDsl = updateDslKeys.includes(key) && isChannels;
    onChange(updates, shouldUpdateDsl);
  };

  const customizationTraySettings = entry?.menu?.customizationTraySettings;

  const isCustomizationTrayNeeded = customizationTraySettings?.isCustomizationTrayNeeded;
  const trayType = customizationTraySettings?.trayType as TrayType;

  const { isMobile } = useMobile();

  useEffect(() => {
    if (isCollections) {
      setCustomizationTray(null);
      setIsFilterTray(false);
      return;
    }

    if (!isFilterTray) {
      isCustomizationTrayNeeded ? setCustomizationTray(trayType) : setCustomizationTray(null);
    }
  }, [isCustomizationTrayNeeded]);

  return (
    <div
      className={classNames('flex flex-wrap items-baseline gap-1 text-heading z-60', {
        'text-lg': !isMobile,
        'text-md': isMobile,
      })}
    >
      {entries.map(entry => {
        const key = typeof entry === 'string' ? entry : (entry.menu?.paramName ?? JSON.stringify(entry));

        return (
          <Fragment key={key}>
            {typeof entry === 'string' ? (
              <span className={classNames({ 'text-md': isMobile, nowrap: !isMobile })}>{entry}</span>
            ) : (
              <SelectionMenuComponent
                menuConfig={entry.menu}
                values={settings}
                onUpdate={updates => handleChange(updates, key)}
                inactive={inactive}
                button={TitleButton}
                useListBoxButtonSlot={true}
              />
            )}
          </Fragment>
        );
      })}
      {!!trayType && !isCollections && customizationTraySettings && <EditTray updateSettings={handleChange} />}
    </div>
  );
}

export { DynamicTitleElement };
